<div class="flex flex-col justify-between h-full">
  <!-- Form Content -->
  <div class="p-6" [formGroup]="form">
    <div class="flex flex-col gap-4">
      <div
        cdkDropList
        (cdkDropListDropped)="drop($event)"
        class="flex flex-col gap-3"
      >
        @for (stopCtrl of stops.controls; track $index) {
        <app-card
          cdkDrag
          [accentStyle]="
            stopCtrl.value.stopType === 'pickup'
              ? 'info'
              : stopCtrl.value.stopType === 'delivery'
              ? 'success'
              : stopCtrl.value.stopType === 'handoff'
              ? 'warning'
              : 'secondary-dark'
          "
          accentPosition="left"
          [editable]="true"
        >
          <div class="flex gap-4">
            <!-- Drag Handle -->
            <div
              cdkDragHandle
              class="flex-shrink-0 pl-2 pt-0.5 text-muted cursor-grab transition-colors hover:text-text"
            >
              <lucide-icon name="GripVertical" size="20"></lucide-icon>
            </div>

            <div class="flex flex-col gap-1 min-w-0 flex-1">
              <div class="flex gap-2">
                <div
                  class="flex items-center justify-center rounded-full h-6 w-6 p-1 shrink-0"
                  [class.bg-secondary-dark]="
                    stopCtrl.value.stopType !== 'pickup' &&
                    stopCtrl.value.stopType !== 'delivery' &&
                    stopCtrl.value.stopType !== 'both' &&
                    stopCtrl.value.stopType !== 'handoff'
                  "
                  [class.bg-info]="stopCtrl.value.stopType === 'pickup'"
                  [class.bg-success]="stopCtrl.value.stopType === 'delivery'"
                  [class.bg-accent]="stopCtrl.value.stopType === 'both'"
                  [class.bg-warning]="stopCtrl.value.stopType === 'handoff'"
                >
                  <lucide-icon
                    [name]="getStopIcon(stopCtrl.value.stopType)"
                    size="16"
                    class="z-10 text-primary-foreground"
                  ></lucide-icon>
                </div>
                <!-- Stop Details -->
                <div class="pb-2 grow flex flex-col gap-1 min-w-0">
                  <div class="flex gap-2 items-center flex-wrap">
                    <app-text weight="medium" color="muted"
                      >Stop {{ stopCtrl.value.stopNumber || "-" }}</app-text
                    >
                    @if (stopCtrl.value.handoffToSegmentId || stopCtrl.value.stopType === 'handoff') {
                      <app-badge
                        variant="warning"
                        badgeStyle="outline"
                        size="xs"
                        title="Relay Split Boundary (removing merges adjacent legs)"
                      ></app-badge>
                    }
                  </div>
                  <app-text weight="semibold" class="truncate">{{
                    stopCtrl.value.companyName || (stopCtrl.value.stopType === 'handoff' ? 'Relay Handoff Location' : 'Facility')
                  }}</app-text>
                </div>
              </div>
              <!-- Address -->
              <div class="flex items-center gap-2 text-sm text-muted">
                <lucide-icon name="MapPin" size="15"></lucide-icon>
                @if (!stopCtrl.value?.address) {
                <app-text size="sm" color="muted">--</app-text>
                } @else {
                <app-text size="sm" color="muted"
                  >{{ stopCtrl.value.address?.street }},
                  {{ stopCtrl.value.address?.street2 || "" }}
                  {{ stopCtrl.value.address?.street2 ? "," : "" }}
                  {{ stopCtrl.value.address?.city }},
                  {{ stopCtrl.value.address?.state }},
                  {{ stopCtrl.value.address?.zip }},
                  {{ stopCtrl.value.address?.country }}</app-text
                >
                }
              </div>
              <!-- Line 2: Appointment Details -->
              <div class="flex gap-2 text-sm text-muted">
                <lucide-icon name="Clock" size="15"></lucide-icon>
                @if (!stopCtrl.value?.appointment || !stopCtrl.value?.appointment?.from?.date) {
                <app-text size="sm" color="muted">Flexible / Open Window</app-text>
                }
                <!-- Strict Appointment -->
                @else if (stopCtrl.value?.appointment?.type === 'strict') {
                <app-badge [title]="stopCtrl.value?.appointment?.type" size="xs" class="self-start font-bold uppercase"></app-badge>
                <div class="flex items-center gap-2">
                  <span class="font-semibold text-text">{{
                    stopCtrl.value?.appointment?.from?.date
                      | date : "EEE, MMM d" : "UTC"
                  }}</span>
                  <div class="w-1 h-1 rounded-full bg-muted"></div>
                  <span class="font-semibold text-text">{{
                    stopCtrl.value?.appointment?.from?.time | formatTime
                  }}</span>
                </div>
                }
                <!-- FCFS Appointment -->
                @else if (stopCtrl.value?.appointment?.type === 'fcfs') {
                <app-badge [title]="stopCtrl.value?.appointment?.type" size="xs" class="self-start font-bold uppercase"></app-badge>
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="font-semibold text-text"
                    >{{
                      stopCtrl.value?.appointment?.from?.date | date : "MMM d" : "UTC"
                    }}
                    &middot;
                    {{
                      stopCtrl.value?.appointment?.from?.time | formatTime
                    }}</span
                  >
                  <lucide-icon name="ArrowRight" size="12"></lucide-icon>
                  <span class="font-semibold text-text"
                    >{{
                      stopCtrl.value?.appointment?.to?.date | date : "MMM d" : "UTC"
                    }}
                    &middot;
                    {{
                      stopCtrl.value?.appointment?.to?.time | formatTime
                    }}</span
                  >
                </div>
                }
              </div>
            </div>
            @if (stops.length > 2 && stopCtrl.value.status !== 'DEPARTED') {
            <app-button
              variant="ghost"
              size="sm"
              [iconOnly]="true"
              iconColor="text-danger"
              icon="Trash2"
              title="Remove Stop"
              class="ml-auto shrink-0"
              (click)="removeStop($index)"
            />
            }
          </div>
        </app-card>
        }
      </div>
      <div class="p-3 rounded-lg bg-surface border border-border flex items-center gap-2">
        <lucide-icon name="Info" size="15" class="text-muted shrink-0"></lucide-icon>
        <app-text size="xs" color="muted">
          Drag stops to reorder or remove unneeded stops. To insert a new stop or relay with full address geocoding, use <strong>Insert Stop</strong> directly on the Route view.
        </app-text>
      </div>
    </div>
  </div>
  <!-- Modal Actions -->
  <div
    class="flex flex-col-reverse lg:flex-row justify-end gap-3 p-2 md:p-4 border-t border-border"
  >
    <app-button variant="secondary" (click)="cancel()" [disabled]="isSaving()">
      Cancel
    </app-button>
    <app-button
      (click)="save()"
      [disabled]="form.invalid || isSaving()"
      [loading]="isSaving()"
    >
      {{ isSaving() ? "Saving..." : "Save Changes" }}
    </app-button>
  </div>
</div>
